import {
  AnnotationIcon,
  ChipIcon,
  ClockIcon,
  HandIcon,
  TranslateIcon,
} from '@heroicons/react/outline';
import type { ComponentType, SVGProps } from 'react';

import agents from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import Halftone from '@/components/agents/Halftone';
import { delay, SectionHeading } from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { BRAND_BG, BUSINESS, useReceptionistT } from './kit';

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

// Two voices on one timeline: the receptionist talks, the caller cuts in at
// bar CUT, and the receptionist stops a beat later.
const BARS = 46;
const CUT = 27;
const STOP = 29;
const shape = (index: number, seed: number) =>
  6 +
  Math.round(
    Math.abs(Math.sin(index * 0.9 + seed) * Math.cos(index * 0.37)) * 30,
  );

export default function ReceptionistVoice() {
  const { t } = useReceptionistT();
  const { ref, state } = useReveal<HTMLDivElement>(0.2);

  const features: { icon: Icon; title: string; detail: string }[] = [
    {
      icon: HandIcon,
      title: t('Callers can interrupt'),
      detail: t('It stops talking the moment the caller does, and listens.'),
    },
    {
      icon: AnnotationIcon,
      title: t('Short, spoken answers'),
      detail: t(
        'A sentence or two at a time, with no links or lists read aloud.',
      ),
    },
    {
      icon: ClockIcon,
      title: t('Remembers the whole call'),
      detail: t(
        'Ask about prices, then say “book it”, and it knows what you mean.',
      ),
    },
    {
      icon: TranslateIcon,
      title: t('Your greeting, your callers’ language'),
      detail: t(
        'Write what it says when it picks up, and set each number’s language from more than 30 languages and accents.',
      ),
    },
    {
      icon: ChipIcon,
      title: t('The same brain as your chat'),
      detail: t(
        'Calls use your agent’s knowledge, instructions and actions, so callers hear the same answers your website gives.',
      ),
    },
  ];

  return (
    <section
      id='conversation'
      className='relative isolate overflow-hidden bg-[#0c0b1c] py-20 sm:py-28'
    >
      <Halftone
        at='0% 0%'
        reach={70}
        className='-z-10 opacity-30 [--ht:rgb(var(--tw-color-primary-800))]'
      />
      <div className='relative mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          invert
          title={t('A real conversation.')}
          tail={t('Not a phone menu.')}
          lead={t(
            'No “press 1 for bookings”. Callers say what they need in their own words, and your receptionist answers in a natural voice.',
          )}
        />

        <div className='mt-16 grid gap-12 lg:grid-cols-12 lg:items-center lg:gap-16'>
          <div
            ref={ref}
            data-reveal={state}
            className={clsxm(agents.reveal, 'min-w-0 lg:col-span-7')}
          >
            <p className='sr-only'>
              {t(
                'Example: the receptionist is listing free times when the caller cuts in to ask about Saturday, and it stops and answers the new question.',
              )}
            </p>
            <InterruptDemo />
          </div>

          <ul className='grid gap-8 sm:grid-cols-2 lg:col-span-5 lg:grid-cols-1 lg:gap-7'>
            {features.map(({ icon: FeatureIcon, title, detail }) => (
              <li key={title} className='flex gap-4'>
                <span className='flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-white/[0.06] text-primary-300 ring-1 ring-inset ring-white/10'>
                  <FeatureIcon className='h-5 w-5' />
                </span>
                <span>
                  <span className='block text-base font-semibold text-white'>
                    {title}
                  </span>
                  <span className='mt-1 block text-[15px] leading-7 text-[#a9a9c6]'>
                    {detail}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function InterruptDemo() {
  const { t } = useReceptionistT();

  return (
    <div
      aria-hidden='true'
      className='overflow-hidden rounded-[28px] bg-white/[0.035] ring-1 ring-inset ring-white/10 sm:rounded-[32px]'
    >
      <div className='flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-6 py-4 sm:px-8'>
        <p className='flex items-center gap-2 text-sm font-medium text-emerald-400'>
          <span className='h-2 w-2 animate-pulse rounded-full bg-emerald-400 motion-reduce:animate-none' />
          {t('Live call')}
          <span className='tabular-nums text-emerald-400/80'>02:31</span>
        </p>
        <p className='text-sm font-semibold text-white'>{BUSINESS}</p>
      </div>

      <div className='px-6 pb-6 pt-12 sm:px-8'>
        {/* One grid for both voices: the label column fits the longest label
            (translations run long), and the marker sits over the bars
            column, so it stays between the same two bars at any width. */}
        <div className='relative grid grid-cols-[minmax(0,max-content)_minmax(0,1fr)] items-center gap-x-4 gap-y-4'>
          <TrackLabel>{t('AI receptionist')}</TrackLabel>
          <Bars barClass='bg-primary-400' from={0} to={STOP} seed={0.4} />
          <TrackLabel>{t('Caller')}</TrackLabel>
          <Bars barClass='bg-white/70' from={CUT} to={BARS} seed={2.1} />
          <div className='pointer-events-none absolute inset-x-0 -top-6 bottom-0 [grid-area:1/2/3/3]'>
            <div
              className={clsxm(
                agents.rv,
                'absolute inset-y-0 border-s border-dashed border-amber-300/70',
              )}
              style={{
                insetInlineStart: `${((CUT - 0.5) / (BARS - 1)) * 100}%`,
                ...delay(500),
              }}
            >
              <span className='absolute -top-2 start-0 w-max max-w-[9rem] -translate-x-1/2 rounded-full bg-amber-300 px-2 py-0.5 text-center text-[11px] font-semibold leading-tight text-[#3a2a00] rtl:translate-x-1/2'>
                {t('Caller cuts in')}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className='space-y-3 border-t border-white/10 px-6 py-6 text-[15px] leading-relaxed sm:px-8'>
        <div
          className={clsxm(agents.rv, 'flex flex-wrap items-center gap-2')}
          style={delay(250)}
        >
          <p className='max-w-[85%] rounded-2xl rounded-es-md bg-primary-500/20 px-4 py-2.5 text-white/55'>
            {t('We have Thursday at 10 a.m. or 2 p.m., or Friday at…')}
          </p>
          <span className='rounded-full bg-amber-300/15 px-2 py-0.5 text-[11px] font-semibold text-amber-200 ring-1 ring-inset ring-amber-300/30'>
            {t('Interrupted')}
          </span>
        </div>
        <div
          className={clsxm(agents.rv, 'flex justify-end')}
          style={delay(650)}
        >
          <p className='max-w-[82%] rounded-2xl rounded-ee-md bg-white/10 px-4 py-2.5 text-white'>
            {t('Sorry, could you do Saturday instead?')}
          </p>
        </div>
        <div className={agents.rv} style={delay(1050)}>
          <p
            className={clsxm(
              'max-w-[85%] rounded-2xl rounded-es-md px-4 py-2.5 text-white',
              BRAND_BG,
            )}
          >
            {t('Saturday at 9 a.m. is free. Shall I book it?')}
          </p>
        </div>
      </div>
    </div>
  );
}

function TrackLabel({ children }: { children: string }) {
  return (
    <span className='line-clamp-2 max-w-[7.5rem] text-xs font-medium leading-tight text-[#9d9dbd] sm:max-w-[10rem]'>
      {children}
    </span>
  );
}

function Bars({
  barClass,
  from,
  to,
  seed,
}: {
  barClass: string;
  from: number;
  to: number;
  seed: number;
}) {
  return (
    <span
      className={clsxm(
        agents.wave,
        agents.waveCenter,
        'flex h-12 min-w-0 items-center justify-between',
      )}
    >
      {Array.from({ length: BARS }, (_, index) => {
        const on = index >= from && index < to;
        return (
          <span
            key={index}
            className={clsxm(
              'w-[2px] flex-none rounded-full sm:w-1',
              on ? barClass : 'bg-white/10',
            )}
            style={{
              height: on ? `${shape(index, seed)}px` : '3px',
              ...(on ? delay(index * 45) : { animation: 'none' }),
            }}
          />
        );
      })}
    </span>
  );
}
